import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { firstValueFrom } from 'rxjs';
import {
    ParkingRightImportService,
    ParkingSpaceDto,
    ParkingZoneDto,
    OperatorActionsService,
    OrganizationService,
    OrganizationDto,
    ListOrganizationRequest,
    ImportSourceFormat,
    ParkerClassification
} from '../../../api/gen';
import { JjamesButtonComponent, JJamesBadgeComponent, BadgeVariant, Role } from 'shared-lib';
import { MigrationSyncLogsComponent } from './migration-sync-logs/migration-sync-logs.component';
import { ParkingTypeUnion, ParkingTypeOption } from '../parking-right.component';
import { RoleService } from '../../auth/role-service';

export interface ParkingRightImportDialogData {
    parkingSpace: ParkingSpaceDto;
    allParkingTypes: ParkingTypeUnion[];
    parkingTypeOptions: ParkingTypeOption[];
}

/** Mirrors the backend's ImportRowStatus enum (serialized as a JSON string). */
type ImportRowStatus = 'Created' | 'Updated' | 'Unchanged' | 'Failed' | 'PartiallyApplied';

interface ImportRowResult {
    rowNumber: number;
    status: ImportRowStatus;
    message: string | null;
    failedStep: string | null;
    licencePlate: string | null;
    plates: string[] | null;
    legacyParkingRightId: string | null;
    createdParkerId: string | null;
    parkerEmail: string | null;
    classification: string | null;
    parkingRightId: string | null;
}

interface ImportCsvResponse {
    applied: boolean;
    count: number;
    results: ImportRowResult[];
}

const STATUS_BADGE_VARIANT: Record<ImportRowStatus, BadgeVariant> = {
    Created: 'success',
    Updated: 'info',
    Unchanged: 'secondary',
    Failed: 'danger',
    PartiallyApplied: 'warning'
};

@Component({
    selector: 'app-parking-right-import',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatTabsModule,
        MigrationSyncLogsComponent,
        JjamesButtonComponent,
        JJamesBadgeComponent
    ],
    templateUrl: './parking-right-import.component.html',
    styleUrl: './parking-right-import.component.scss'
})
export class ParkingRightImportComponent implements OnInit {
    private fb = inject(FormBuilder);
    private parkingRightImportService = inject(ParkingRightImportService);
    private organizationService = inject(OrganizationService);
    private dialogRef = inject(MatDialogRef<ParkingRightImportComponent>);
    private dialogData = inject<ParkingRightImportDialogData>(MAT_DIALOG_DATA);
    private snackBar = inject(MatSnackBar);
    private operatorActionsService = inject(OperatorActionsService);
    private roleService = inject(RoleService);

    form!: FormGroup;
    isLoading = signal(false);
    allParkingTypes = signal<ParkingTypeUnion[]>([]);
    parkingTypeOptions = signal<ParkingTypeOption[]>([]);
    selectedFile: File | null = null;
    fileName = signal<string | null>(null);
    operatorId = signal<string | null>(null);
    canViewSyncLogs = signal(false);

    readonly sourceOptions: { value: ImportSourceFormat; label: string }[] = [
        { value: ImportSourceFormat.Canonical, label: 'Canonical (generic CSV)' },
        { value: ImportSourceFormat.Jku, label: 'JKU (Dauerparker export)' }
    ];

    readonly classificationOptions: { value: ParkerClassification; label: string }[] = [
        { value: ParkerClassification.Employee, label: 'Employee' },
        { value: ParkerClassification.Student, label: 'Student' }
    ];

    // ---- Organization search ----
    organizationSearch = signal('');
    organizationResults = signal<OrganizationDto[]>([]);
    selectedOrganization = signal<OrganizationDto | null>(null);
    isSearchingOrganizations = signal(false);
    private orgSearchDebounce: ReturnType<typeof setTimeout> | null = null;

    // ---- Dry-run / apply results ----
    hasPreviewed = signal(false);
    importResults = signal<ImportRowResult[]>([]);
    lastApplied = signal(false);
    resultStatusFilter = signal<ImportRowStatus | 'all'>('all');

    resultCounts = computed(() => {
        const counts: Record<ImportRowStatus, number> = {
            Created: 0, Updated: 0, Unchanged: 0, Failed: 0, PartiallyApplied: 0
        };
        for (const row of this.importResults()) {
            counts[row.status]++;
        }
        return counts;
    });

    filteredImportResults = computed(() => {
        const filter = this.resultStatusFilter();
        const rows = this.importResults();
        return filter === 'all' ? rows : rows.filter(r => r.status === filter);
    });

    get parkingSpace(): ParkingSpaceDto {
        return this.dialogData.parkingSpace;
    }

    get availableZones(): ParkingZoneDto[] {
        return this.parkingSpace?.parkingZones || [];
    }

    ngOnInit(): void {
        this.initializeForm();
        this.checkImportAccessFromApi();
    }

    private async checkImportAccessFromApi(): Promise<void> {
        try {
            const [isAdmin, isOperatorAdmin] = await Promise.all([
                this.roleService.checkAccess(Role.Administrator),
                this.roleService.checkAccess(Role.OperatorAdmin)
            ]);

            if (!isAdmin && !isOperatorAdmin) {
                this.snackBar.open('You are not allowed to access Parking Right import.', 'Close', { duration: 4000 });
                this.dialogRef.close(false);
                return;
            }

            this.canViewSyncLogs.set(isAdmin);
            this.allParkingTypes.set(this.dialogData.allParkingTypes);
            this.parkingTypeOptions.set(this.dialogData.parkingTypeOptions);

            this.operatorActionsService.operatorMyAccountGet().subscribe({
                next: (operator) => this.operatorId.set(operator.id ?? null),
                error: (error) => console.error('Error fetching operator id:', error)
            });
        } catch (error) {
            console.error('Error checking import access:', error);
            this.snackBar.open('Failed to verify import access.', 'Close', { duration: 4000 });
            this.dialogRef.close(false);
        }
    }

    private initializeForm(): void {
        this.form = this.fb.group({
            parkingTypeId: ['', Validators.required],
            // A set of zones, not one: a single source area can map to several of our zones
            // ("Großer Parkplatz" is Altenberger Straße *and* Science Park Freifläche). Running the
            // import once per zone does not work — the second run matches the first run's right on
            // (plate, parking type) and edits it, so the parker keeps only the last zone.
            zoneIds: [[] as string[]],
            source: [ImportSourceFormat.Canonical, Validators.required],
            classification: [ParkerClassification.Employee, Validators.required],
            // Empty = no cap: the right admits every plate on it simultaneously, which is how every
            // import behaved before this existed. Set to 1 for "one user, one parking right".
            maxConcurrentCars: [null as number | null, [Validators.min(1)]],
            file: ['', Validators.required]
        });
    }

    /** Drives the zone hint; read from the control so it tracks the multi-select live. */
    protected selectedZoneCount(): number {
        return (this.form?.get('zoneIds')?.value as string[] | null)?.length ?? 0;
    }

    getParkingTypeName(item: ParkingTypeUnion): string {
        return item.parkingType?.type?.name || 'Unknown';
    }

    getParkingTypeId(item: ParkingTypeUnion): string | undefined {
        return item.parkingType?.id;
    }

    getParkingTypeCategory(item: ParkingTypeUnion): string {
        const categoryMap: Record<string, string> = {
            Default: 'Default Exemption',
            Exemption: 'Custom Exemption',
            AssignedZoneParkers: 'Assigned Zone Parkers',
            PoolParking: 'Pool Parking',
            LongTerm: 'Long-Term'
        };
        const typeEnum = item.parkingType?.type?.type;
        return typeEnum ? (categoryMap[typeEnum] ?? '') : '';
    }

    getParkingTypeOperatorName(item: ParkingTypeUnion): string {
        const op = item.parkingType?.operator;
        if (!op) return '';
        return [op.firstName, op.lastName].filter(Boolean).join(' ').trim();
    }

    getParkingTypeOrgName(item: ParkingTypeUnion): string {
        return item.parkingType?.organization?.name || '';
    }

    // ---- Organization search ----
    onOrganizationSearchChange(value: string): void {
        this.organizationSearch.set(value);
        this.selectedOrganization.set(null);
        if (this.orgSearchDebounce) clearTimeout(this.orgSearchDebounce);
        this.orgSearchDebounce = setTimeout(() => this.searchOrganizations(), 300);
    }

    private async searchOrganizations(): Promise<void> {
        const name = this.organizationSearch().trim();
        if (!name) {
            this.organizationResults.set([]);
            return;
        }
        this.isSearchingOrganizations.set(true);
        try {
            const request: ListOrganizationRequest = { name, pageIndex: 0, pageSize: 10, includeDeleted: false };
            const result = await firstValueFrom(this.organizationService.organizationsFilterPost(request));
            this.organizationResults.set(result?.items ?? []);
        } catch (e) {
            console.error('Organization search failed:', e);
        } finally {
            this.isSearchingOrganizations.set(false);
        }
    }

    selectOrganization(org: OrganizationDto): void {
        this.selectedOrganization.set(org);
        this.organizationSearch.set(org.name ?? '');
        this.organizationResults.set([]);
    }

    clearSelectedOrganization(): void {
        this.selectedOrganization.set(null);
        this.organizationSearch.set('');
    }

    // ---- File handling ----
    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            if (!file.name.toLowerCase().endsWith('.csv')) {
                this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
                return;
            }

            this.selectedFile = file;
            this.fileName.set(file.name);
            this.form.patchValue({ file: file.name });
            this.resetResults();
        }
    }

    clearFile(): void {
        this.selectedFile = null;
        this.fileName.set(null);
        this.form.patchValue({ file: '' });
        this.resetResults();
    }

    private resetResults(): void {
        this.hasPreviewed.set(false);
        this.importResults.set([]);
    }

    // ---- Preview (dry-run) / Apply ----
    onPreview(): void {
        this.runImport(false);
    }

    onApply(): void {
        this.runImport(true);
    }

    private runImport(apply: boolean): void {
        if (this.form.invalid || !this.selectedFile) {
            this.snackBar.open('Please fill all required fields and select a file', 'Close', { duration: 3000 });
            return;
        }
        if (!this.selectedOrganization()) {
            this.snackBar.open('Please select the organization these members belong to', 'Close', { duration: 3000 });
            return;
        }

        this.isLoading.set(true);

        const formValue = this.form.value;

        // Positional call into the generated client, so every argument is named in a comment:
        // inserting a query parameter on the controller silently shifts everything after it, and
        // the compiler only catches it when the types happen to differ.
        const zoneIds: string[] = formValue.zoneIds ?? [];

        this.parkingRightImportService.parkingrightsImportCsvPost(
            apply,                                        // apply
            formValue.source,                             // source
            formValue.classification,                     // classification
            formValue.parkingTypeId,                      // defaultParkingTypeId
            this.parkingSpace.id!,                        // defaultSpaceId
            zoneIds.length ? zoneIds : undefined,         // defaultZoneIds
            undefined,                                    // defaultZoneId (deprecated single-zone form)
            this.selectedOrganization()!.id,              // defaultOrganizationId
            this.operatorId() ?? undefined,               // operatorId
            formValue.maxConcurrentCars ?? undefined,     // maxConcurrentCars
            this.selectedFile                             // file
        ).subscribe({
            next: (response: unknown) => {
                const result = response as ImportCsvResponse;
                this.importResults.set(result?.results ?? []);
                this.resultStatusFilter.set('all');
                this.hasPreviewed.set(true);
                this.lastApplied.set(apply);
                this.isLoading.set(false);

                if (apply) {
                    const counts = this.resultCounts();
                    this.snackBar.open(
                        `Import applied: ${counts.Created} created, ${counts.Updated} updated, ${counts.Unchanged} unchanged, ${counts.Failed + counts.PartiallyApplied} failed.`,
                        'Close', { duration: 5000 });
                }
            },
            error: (error) => {
                console.error('Error importing parking rights:', error);
                const errorMessage = error?.error?.message || error?.error?.title || 'Failed to import parking rights';
                this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
                this.isLoading.set(false);
            }
        });
    }

    downloadErrorRows(): void {
        const failedRows = this.importResults().filter(r => r.status === 'Failed' || r.status === 'PartiallyApplied');
        if (failedRows.length === 0) return;

        const header = ['RowNumber', 'Status', 'FailedStep', 'ParkerEmail', 'Plates', 'Message'];
        const lines = [header.join(',')];
        for (const row of failedRows) {
            const cells = [
                row.rowNumber,
                row.status,
                row.failedStep ?? '',
                row.parkerEmail ?? '',
                (row.plates ?? []).join(' '),
                (row.message ?? '').replace(/"/g, '""')
            ];
            lines.push(cells.map(c => `"${c}"`).join(','));
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'parking-right-import-errors.csv';
        link.click();
        URL.revokeObjectURL(url);
    }

    statusBadgeVariant(status: ImportRowStatus): BadgeVariant {
        return STATUS_BADGE_VARIANT[status];
    }

    toggleResultStatusFilter(status: ImportRowStatus): void {
        this.resultStatusFilter.set(this.resultStatusFilter() === status ? 'all' : status);
    }

    onCancel(): void {
        this.dialogRef.close(this.lastApplied() && this.hasPreviewed());
    }
}
